import { EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE, EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE } from '@immo/api-contract';
import type { PlaceholderLabel } from '@immo/components';
import { sanitiseEmailTemplateHtml } from '@/lib/email/sanitise-email-template-html';

/** What the editor page needs of a chip-capable field: put the caret back and insert nodes there. */
export type ChipInsertHandle = {
  focus(): void;
  /** Restores the saved range (or the end), inserts in order, places the caret after, emits `onChange`. */
  insertNodesAtCaret(nodes: ReadonlyArray<Node>): void;
  root(): HTMLElement | null;
};

const CHIP_SELECTOR = `[${EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE}], [${EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE}]`;
const CHIP_CLASS =
  'mx-0.5 inline-flex items-center rounded-sm border border-border bg-subtle px-1 font-mono text-xs text-text-secondary';

function createChip(attribute: string, value: string, text: string, extraClass: string): HTMLSpanElement {
  const chip = document.createElement('span');

  chip.setAttribute(attribute, value);
  chip.setAttribute('contenteditable', 'false');
  chip.className = `${CHIP_CLASS} ${extraClass}`.trim();
  chip.textContent = text;

  return chip;
}

export function createPlaceholderChip(key: string, labels: PlaceholderLabel): HTMLSpanElement {
  return createChip(EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE, key, `{${labels(key)}}`, '');
}

export function createFillFieldChip(label: string): HTMLSpanElement {
  return createChip(EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE, label, `[${label}]`, 'border-dashed');
}

function displayChipFor(canonical: Element, labels: PlaceholderLabel): HTMLSpanElement {
  const placeholder = canonical.getAttribute(EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE);

  if (placeholder !== null) return createPlaceholderChip(placeholder, labels);

  return createFillFieldChip(canonical.getAttribute(EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE) ?? '');
}

/** Fills `root` from stored markup, with every canonical chip swapped for its display chip. */
export function hydrateChips(root: HTMLElement, markup: string, labels: PlaceholderLabel): void {
  // Stored markup is server-sanitised; it is sanitised again here because it becomes live DOM in an owner's session.
  root.innerHTML = sanitiseEmailTemplateHtml(markup);

  for (const canonical of Array.from(root.querySelectorAll(CHIP_SELECTOR))) {
    canonical.replaceWith(displayChipFor(canonical, labels));
  }
}

/** The editor HTML with every display chip back to the canonical empty span. */
export function serialiseChips(root: HTMLElement): string {
  const clone = root.cloneNode(true) as HTMLElement;

  for (const chip of Array.from(clone.querySelectorAll(CHIP_SELECTOR))) {
    const canonical = document.createElement('span');
    const placeholder = chip.getAttribute(EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE);

    if (placeholder !== null) {
      canonical.setAttribute(EMAIL_TEMPLATE_PLACEHOLDER_ATTRIBUTE, placeholder);
    } else {
      canonical.setAttribute(
        EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE,
        chip.getAttribute(EMAIL_TEMPLATE_FILL_FIELD_ATTRIBUTE) ?? '',
      );
    }

    chip.replaceWith(canonical);
  }

  return clone.innerHTML;
}

/** `hydrateChips` into a detached element, for a field that seeds its own `innerHTML` once. */
export function hydratedChipHtml(markup: string, labels: PlaceholderLabel): string {
  const holder = document.createElement('div');

  hydrateChips(holder, markup, labels);

  return holder.innerHTML;
}

/** Remembers the last caret inside `root` — the menu, its items and the dialog all take focus, so "the caret now" is gone by insert time. */
export function trackCaret(root: HTMLElement, onRange: (range: Range) => void): () => void {
  const onSelectionChange = () => {
    const selection = document.getSelection();

    if (selection === null || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);

    if (root.contains(range.commonAncestorContainer)) onRange(range.cloneRange());
  };

  document.addEventListener('selectionchange', onSelectionChange);

  return () => document.removeEventListener('selectionchange', onSelectionChange);
}

/** Inserts `nodes` at `saved` (or the end of `root`), a space after a trailing chip, and leaves the caret after them. */
export function insertNodesAt(root: HTMLElement, saved: Range | null, nodes: ReadonlyArray<Node>): Range {
  const range =
    saved !== null && root.contains(saved.commonAncestorContainer) ? saved.cloneRange() : document.createRange();

  if (saved === null || !root.contains(saved.commonAncestorContainer)) {
    range.selectNodeContents(root);
    range.collapse(false);
  }

  range.deleteContents();

  const last = nodes[nodes.length - 1];
  const inserted =
    last instanceof Element && last.matches(CHIP_SELECTOR) ? [...nodes, document.createTextNode(' ')] : nodes;

  for (const node of inserted) {
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
  }

  const selection = document.getSelection();

  selection?.removeAllRanges();
  selection?.addRange(range);

  return range.cloneRange();
}

/** The chip a Backspace (`'before'`) or Delete (`'after'`) at the collapsed caret would remove, if the neighbour is one. */
export function adjacentChip(root: HTMLElement, side: 'before' | 'after'): Element | null {
  const selection = document.getSelection();

  if (selection === null || selection.rangeCount === 0 || !selection.isCollapsed) return null;

  const { startContainer, startOffset } = selection.getRangeAt(0);

  if (!root.contains(startContainer)) return null;

  const neighbour = neighbourOf(startContainer, startOffset, side);

  return neighbour instanceof Element && neighbour.matches(CHIP_SELECTOR) ? neighbour : null;
}

function neighbourOf(container: Node, offset: number, side: 'before' | 'after'): Node | null {
  if (container.nodeType === Node.TEXT_NODE) {
    const length = container.textContent?.length ?? 0;

    if (side === 'before') return offset === 0 ? container.previousSibling : null;

    return offset === length ? container.nextSibling : null;
  }

  return side === 'before' ? (container.childNodes[offset - 1] ?? null) : (container.childNodes[offset] ?? null);
}
